import React from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Lightning } from "@phosphor-icons/react";

/**
 * Cinematic dark hero — inspired by the user's "Your trades tell a story"
 * concept. Black canvas + animated gold + blue light beams sweeping in
 * from screen edges, a starfield, and a tilted 3D dashboard preview.
 *
 * Motion is all CSS keyframes so it stays 60fps without JS.
 * Respects `prefers-reduced-motion` via Tailwind's `motion-reduce:` variant.
 */
export default function CinematicHero() {
  return (
    <section
      className="relative overflow-hidden bg-black text-white"
      data-testid="cinematic-hero"
      style={{ minHeight: "min(880px, 100vh)" }}>
      {/* Global keyframes local to this section */}
      <style>{`
        @keyframes tj-beam-sweep {
          0% { transform: translate(-40%, -20%) rotate(18deg); opacity: 0; }
          20% { opacity: .55; }
          50% { opacity: .9; }
          80% { opacity: .55; }
          100% { transform: translate(40%, 20%) rotate(18deg); opacity: 0; }
        }
        @keyframes tj-beam-sweep-2 {
          0% { transform: translate(20%, 60%) rotate(-14deg); opacity: 0; }
          25% { opacity: .55; }
          55% { opacity: .85; }
          100% { transform: translate(-20%, -60%) rotate(-14deg); opacity: 0; }
        }
        @keyframes tj-twinkle {
          0%, 100% { opacity: .15; }
          50% { opacity: 1; }
        }
        @keyframes tj-tilt-float {
          0%, 100% { transform: perspective(1400px) rotateY(-14deg) rotateX(6deg) translateY(0px); }
          50%      { transform: perspective(1400px) rotateY(-14deg) rotateX(6deg) translateY(-12px); }
        }
        @keyframes tj-card-glow {
          0%,100% { box-shadow: 0 40px 120px -20px rgba(59,130,246,0.55), 0 0 60px rgba(59,130,246,0.35); }
          50%     { box-shadow: 0 40px 120px -20px rgba(59,130,246,0.85), 0 0 90px rgba(59,130,246,0.55); }
        }
        @keyframes tj-pulse-badge {
          0%,100% { box-shadow: 0 0 0 0 rgba(90,162,255,0.55); }
          50%     { box-shadow: 0 0 0 8px rgba(90,162,255,0); }
        }
      `}</style>

      {/* Starfield — static SVG dots twinkling at different rates */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {Array.from({ length: 55 }).map((_, i) => {
          const seed = (i * 61) % 100;
          const size = 1 + (seed % 3);
          const dur = 3 + (seed % 5);
          return (
            <span
              key={i}
              className="absolute rounded-full bg-white motion-reduce:animate-none"
              style={{
                width: size, height: size,
                left: ((i * 137) % 100) + "%",
                top: ((i * 89) % 100) + "%",
                opacity: 0.5,
                animation: `tj-twinkle ${dur}s ease-in-out ${(seed % 40) / 10}s infinite`,
              }}
            />
          );
        })}
      </div>

      {/* Gold beam sweeping from bottom-right */}
      <div
        className="absolute inset-0 pointer-events-none motion-reduce:animate-none"
        aria-hidden="true"
        style={{ animation: "tj-beam-sweep 14s ease-in-out infinite" }}>
        <div className="absolute right-[-10%] top-[-10%] h-[140%] w-[46%] blur-3xl"
          style={{
            background: "linear-gradient(200deg, transparent 20%, rgba(245,196,67,0.35) 45%, rgba(245,196,67,0.55) 55%, transparent 80%)",
            transform: "rotate(18deg)",
          }} />
      </div>

      {/* Blue beam sweeping from top-right */}
      <div
        className="absolute inset-0 pointer-events-none motion-reduce:animate-none"
        aria-hidden="true"
        style={{ animation: "tj-beam-sweep-2 18s ease-in-out infinite", animationDelay: "3s" }}>
        <div className="absolute right-[10%] top-[-30%] h-[160%] w-[30%] blur-3xl"
          style={{
            background: "linear-gradient(200deg, transparent 20%, rgba(59,130,246,0.45) 50%, transparent 85%)",
            transform: "rotate(-14deg)",
          }} />
      </div>

      {/* Central content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-center">
        {/* Left column — copy */}
        <div className="max-w-xl">
          {/* Wordmark — with tiny "by Yash Khajania" credit tucked above the final letter */}
          <Link to="/" className="inline-flex items-center gap-2.5" data-testid="cinematic-brand">
            <span className="w-10 h-10 rounded-xl grid place-items-center shrink-0"
              style={{ background: "linear-gradient(135deg,#3b82f6,#6366f1)", boxShadow: "0 8px 24px -6px rgba(59,130,246,0.6)" }}>
              <Lightning size={20} weight="fill" className="text-white" />
            </span>
            <span className="relative inline-block font-display font-black text-2xl tracking-tight leading-none pt-2.5">
              <span
                className="absolute right-0 -top-1 text-[8px] font-semibold tracking-[0.15em] uppercase text-blue-300/90 whitespace-nowrap"
                style={{ letterSpacing: "0.08em" }}>
                by Yash Khajania
              </span>
              Tradelog
            </span>
          </Link>

          {/* Live badge — pulsing dot. `flex w-fit` (not inline-flex) so it drops
              to its own line beneath the wordmark instead of colliding with it. */}
          <div className="mt-6 sm:mt-8 flex w-fit max-w-full items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] backdrop-blur px-3 py-1.5 text-[8px] sm:text-[10px] uppercase tracking-[0.14em] sm:tracking-[0.25em] text-neutral-300"
               style={{ animation: "tj-pulse-badge 2.4s ease-in-out infinite" }}
               data-testid="cinematic-eyebrow">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
            <span className="whitespace-nowrap">LIVE · Built by a trader, for traders — no fluff</span>
          </div>

          {/* Headline — journal-first positioning. No "prop firm" phrasing that
              might imply we run real challenges. */}
          <h1 className="mt-6 font-display font-black uppercase tracking-tight leading-[0.92]"
              style={{ fontSize: "clamp(32px, 7vw, 104px)" }}>
            <span className="text-white">The Trading</span><br />
            <span className="text-white">Journal That</span><br />
            <span className="bg-gradient-to-r from-[#5aa2ff] via-[#a78bfa] to-[#f5c443] bg-clip-text text-transparent">Makes You Better.</span>
          </h1>
          <p className="mt-4 sm:mt-6 font-display italic font-light text-[#5aa2ff]"
             style={{ fontSize: "clamp(15px, 2.2vw, 32px)" }}>
            Journal · Analyse · Practice — all in one place.
          </p>
          <p className="mt-4 sm:mt-6 text-neutral-400 leading-relaxed text-sm sm:text-lg max-w-md">
            Log every trade, spot the patterns behind your best and worst days, and
            sharpen your discipline in a risk-free virtual challenge that mirrors
            professional funded-trader rules. No real money — just real growth.
          </p>

          {/* CTA */}
          <div className="mt-10">
            <Link to="/signup" data-testid="cinematic-cta">
              <Button
                size="lg"
                className="rounded-full h-14 px-8 text-base font-bold tracking-wide bg-[#1a3a8a] hover:bg-[#254bad] text-white uppercase relative overflow-hidden group"
                style={{ boxShadow: "0 0 0 1px rgba(90,162,255,0.5), 0 20px 40px -12px rgba(59,130,246,0.55)" }}>
                <span className="relative z-10 flex items-center gap-2">
                  <Lightning size={16} weight="fill" />
                  Start Your Journal Free
                </span>
                <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"
                  style={{ background: "linear-gradient(120deg, transparent, rgba(255,255,255,0.25), transparent)" }} />
              </Button>
            </Link>
            <p className="mt-3 text-neutral-500 text-xs sm:text-sm">
              thetradlog.com — 14-day free trial, no card required.
            </p>
          </div>
        </div>

        {/* Right column — tilted dashboard mockup */}
        <div className="relative flex justify-center lg:justify-end">
          <div
            className="relative w-full max-w-[560px] motion-reduce:animate-none"
            style={{
              animation: "tj-tilt-float 8s ease-in-out infinite",
              transformStyle: "preserve-3d",
            }}>
            {/* Glow behind card */}
            <div className="absolute inset-8 rounded-3xl blur-3xl motion-reduce:animate-none"
              style={{
                background: "radial-gradient(closest-side, rgba(59,130,246,0.6), transparent 70%)",
                animation: "tj-card-glow 5s ease-in-out infinite",
              }}
              aria-hidden="true" />

            {/* Dashboard card */}
            <div className="relative rounded-2xl border border-blue-400/25 overflow-hidden"
              style={{
                background: "linear-gradient(145deg, rgba(20,25,45,0.95), rgba(8,10,20,0.98))",
                backdropFilter: "blur(20px)",
                boxShadow: "0 40px 120px -20px rgba(59,130,246,0.55), inset 0 1px 0 rgba(255,255,255,0.08)",
              }}>
              {/* Titlebar */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 text-[10px] text-neutral-400">
                <span>▸ Trading Journal</span>
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span>· · ·</span>
                </span>
              </div>

              {/* Weekly P&L calendar row */}
              <div className="px-3 sm:px-4 pt-4">
                <p className="text-[10px] tracking-widest uppercase text-neutral-500 mb-2">Weekly P&amp;L calendar</p>
                <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
                  {[
                    { d: "May", v: "+3.0", tone: "emerald" },
                    { d: "Mon", v: "-35.2", tone: "red" },
                    { d: "Tue", v: "-1.5", tone: "red" },
                    { d: "Wed", v: "+2.8", tone: "emerald" },
                    { d: "Thu", v: "-18.8", tone: "red" },
                    { d: "Fri", v: "-22.8", tone: "red" },
                    { d: "Sat", v: "-26.6", tone: "red" },
                  ].map(({ d, v, tone }, i) => (
                    <div key={i}
                      className={`rounded-md px-0.5 py-1.5 sm:p-2 text-center border overflow-hidden ${
                        tone === "emerald"
                          ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                          : "bg-red-500/15 border-red-500/40 text-red-300"
                      }`}>
                      <div className="text-[7px] sm:text-[8px] uppercase tracking-widest opacity-70">{d}</div>
                      <div className="text-[9px] sm:text-[11px] font-bold font-mono tabular-nums mt-0.5 whitespace-nowrap leading-none">{v}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Equity curve — SVG line */}
              <div className="px-4 py-4 mt-2 border-t border-white/5">
                <p className="text-[10px] tracking-widest uppercase text-neutral-500 mb-1">Equity curve</p>
                <svg viewBox="0 0 400 130" className="w-full h-32">
                  <defs>
                    <linearGradient id="tj-eq" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0" stopColor="#5aa2ff" stopOpacity="0.55" />
                      <stop offset="1" stopColor="#5aa2ff" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  {/* Grid */}
                  {[0, 30, 60, 90, 120].map(y => (
                    <line key={y} x1="0" y1={y} x2="400" y2={y} stroke="rgba(255,255,255,0.05)" />
                  ))}
                  {/* Line */}
                  <path
                    d="M 0 110 L 40 108 L 70 118 L 100 100 L 130 90 L 160 70 L 190 82 L 220 55 L 250 60 L 280 40 L 310 45 L 340 25 L 370 32 L 400 15"
                    fill="none" stroke="#5aa2ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path
                    d="M 0 110 L 40 108 L 70 118 L 100 100 L 130 90 L 160 70 L 190 82 L 220 55 L 250 60 L 280 40 L 310 45 L 340 25 L 370 32 L 400 15 L 400 130 L 0 130 Z"
                    fill="url(#tj-eq)" />
                </svg>
                {/* Y-axis labels */}
                <div className="flex justify-between text-[8px] text-neutral-500 -mt-1">
                  <span>Sun</span><span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span>
                </div>
              </div>

              {/* Trades feed */}
              <div className="px-4 py-3 border-t border-white/5">
                <p className="text-[10px] tracking-widest uppercase text-neutral-500 mb-2">Trades</p>
                <div className="space-y-1">
                  {[
                    { t: "12:00 PM", s: "EURUSD 1H", p: "+209.20", tone: "emerald" },
                    { t: "12:30 PM", s: "EURUSD 12H", p: "+178.06", tone: "emerald" },
                    { t: "12:55 PM", s: "EURUSD 12H", p: "-4.20", tone: "red" },
                    { t: "12:39 PM", s: "EURUSD 12H", p: "-60.87", tone: "red" },
                  ].map((r, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_auto] items-center gap-2 text-[10px] font-mono">
                      <span className="text-neutral-400">{r.t}</span>
                      <span className="text-neutral-300 text-center">{r.s}</span>
                      <span className={`text-right ${r.tone === "emerald" ? "text-emerald-400" : "text-red-400"}`}>{r.p}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
